/**
 * Phone number validation utility with country-specific rules
 */

export interface PhoneValidationRule {
  countryCode: string;
  country: string;
  iso2: string;
  minLength: number;
  maxLength: number;
  pattern: RegExp;
  format: string;
  example: string;
}

/**
 * Convert ISO 3166-1 alpha-2 code to flag emoji
 */
export const getCountryFlag = (iso2: string): string =>
  iso2
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');

export const phoneValidationRules: PhoneValidationRule[] = [
  {
    countryCode: '+1',
    country: 'US/Canada',
    iso2: 'US',
    minLength: 10,
    maxLength: 10,
    pattern: /^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
    format: '(XXX) XXX-XXXX',
    example: '(555) 123-4567',
  },
  {
    countryCode: '+91',
    country: 'India',
    iso2: 'IN',
    minLength: 10,
    maxLength: 10,
    pattern: /^[6-9]\d{9}$/,
    format: 'XXXXX XXXXX',
    example: '98765 43210',
  },
  {
    countryCode: '+44',
    country: 'UK',
    iso2: 'GB',
    minLength: 10,
    maxLength: 11,
    pattern: /^[1-9]\d{8,9}$/,
    format: 'XXXX XXX XXXX',
    example: '7700 900123',
  },
  {
    countryCode: '+61',
    country: 'Australia',
    iso2: 'AU',
    minLength: 9,
    maxLength: 9,
    pattern: /^[2-578]\d{8}$/,
    format: 'X XXXX XXXX',
    example: '4 1234 5678',
  },
  {
    countryCode: '+49',
    country: 'Germany',
    iso2: 'DE',
    minLength: 10,
    maxLength: 12,
    pattern: /^[1-9]\d{9,11}$/,
    format: 'XXX XXXXXXXX',
    example: '151 23456789',
  },
  {
    countryCode: '+33',
    country: 'France',
    iso2: 'FR',
    minLength: 9,
    maxLength: 9,
    pattern: /^[1-9]\d{8}$/,
    format: 'X XX XX XX XX',
    example: '6 12 34 56 78',
  },
  {
    countryCode: '+86',
    country: 'China',
    iso2: 'CN',
    minLength: 11,
    maxLength: 11,
    pattern: /^1[3-9]\d{9}$/,
    format: 'XXX XXXX XXXX',
    example: '138 0013 8000',
  },
  {
    countryCode: '+81',
    country: 'Japan',
    iso2: 'JP',
    minLength: 10,
    maxLength: 11,
    pattern: /^[7-9]\d{9,10}$/,
    format: 'XXX-XXXX-XXXX',
    example: '90-1234-5678',
  },
  {
    countryCode: '+52',
    country: 'Mexico',
    iso2: 'MX',
    minLength: 10,
    maxLength: 10,
    pattern: /^[1-9]\d{9}$/,
    format: 'XXX XXX XXXX',
    example: '55 1234 5678',
  },
  {
    countryCode: '+55',
    country: 'Brazil',
    iso2: 'BR',
    minLength: 10,
    maxLength: 11,
    pattern: /^[1-9]\d{9,10}$/,
    format: '(XX) XXXXX-XXXX',
    example: '(11) 91234-5678',
  },
  {
    countryCode: '+7',
    country: 'Russia',
    iso2: 'RU',
    minLength: 10,
    maxLength: 10,
    pattern: /^[3-9]\d{9}$/,
    format: 'XXX XXX-XX-XX',
    example: '912 345-67-89',
  },
  {
    countryCode: '+34',
    country: 'Spain',
    iso2: 'ES',
    minLength: 9,
    maxLength: 9,
    pattern: /^[6-9]\d{8}$/,
    format: 'XXX XX XX XX',
    example: '612 34 56 78',
  },
  {
    countryCode: '+39',
    country: 'Italy',
    iso2: 'IT',
    minLength: 9,
    maxLength: 10,
    pattern: /^[3]\d{8,9}$/,
    format: 'XXX XXX XXXX',
    example: '320 123 4567',
  },
  {
    countryCode: '+31',
    country: 'Netherlands',
    iso2: 'NL',
    minLength: 9,
    maxLength: 9,
    pattern: /^[6]\d{8}$/,
    format: 'X XX XX XX XX',
    example: '6 12 34 56 78',
  },
  {
    countryCode: '+46',
    country: 'Sweden',
    iso2: 'SE',
    minLength: 9,
    maxLength: 9,
    pattern: /^[7]\d{8}$/,
    format: 'XXX-XXX XXX',
    example: '701-234 567',
  },
  {
    countryCode: '+47',
    country: 'Norway',
    iso2: 'NO',
    minLength: 8,
    maxLength: 8,
    pattern: /^[4-9]\d{7}$/,
    format: 'XXX XX XXX',
    example: '412 34 567',
  },
  {
    countryCode: '+45',
    country: 'Denmark',
    iso2: 'DK',
    minLength: 8,
    maxLength: 8,
    pattern: /^[2-9]\d{7}$/,
    format: 'XX XX XX XX',
    example: '20 12 34 56',
  },
  {
    countryCode: '+41',
    country: 'Switzerland',
    iso2: 'CH',
    minLength: 9,
    maxLength: 9,
    pattern: /^[7]\d{8}$/,
    format: 'XXX XXX XXX',
    example: '781 234 567',
  },
  {
    countryCode: '+43',
    country: 'Austria',
    iso2: 'AT',
    minLength: 10,
    maxLength: 11,
    pattern: /^[6-9]\d{9,10}$/,
    format: 'XXX XXXXXXX',
    example: '664 1234567',
  },
  {
    countryCode: '+32',
    country: 'Belgium',
    iso2: 'BE',
    minLength: 9,
    maxLength: 9,
    pattern: /^[4]\d{8}$/,
    format: 'XXX XX XX XX',
    example: '470 12 34 56',
  },
];

/**
 * Get validation rule for a specific country code
 */
export const getValidationRule = (countryCode: string): PhoneValidationRule | null => {
  return phoneValidationRules.find(rule => rule.countryCode === countryCode) || null;
};

/**
 * Validate phone number based on country code
 */
export const validatePhoneNumber = (
  phoneNumber: string,
  countryCode: string
): {
  isValid: boolean;
  message?: string;
} => {
  // Remove all non-digit characters
  const cleanNumber = phoneNumber.replace(/\D/g, '');

  if (!cleanNumber) {
    return {
      isValid: false,
      message: 'Phone number is required',
    };
  }

  const rule = getValidationRule(countryCode);

  if (!rule) {
    // Generic validation for unsupported countries
    if (cleanNumber.length < 7 || cleanNumber.length > 15) {
      return {
        isValid: false,
        message: 'Phone number must be between 7-15 digits',
      };
    }
    return { isValid: true };
  }

  // Check length
  if (cleanNumber.length < rule.minLength || cleanNumber.length > rule.maxLength) {
    return {
      isValid: false,
      message: `Phone number must be ${rule.minLength}${rule.minLength !== rule.maxLength ? `-${rule.maxLength}` : ''} digits for ${rule.country}`,
    };
  }

  // Check pattern
  if (!rule.pattern.test(cleanNumber)) {
    return {
      isValid: false,
      message: `Invalid phone number format for ${rule.country}. Example: ${rule.example}`,
    };
  }

  return { isValid: true };
};

/**
 * Format phone number based on country code
 */
export const formatPhoneNumber = (phoneNumber: string, countryCode: string): string => {
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  const rule = getValidationRule(countryCode);

  if (!rule || !cleanNumber) {
    return phoneNumber;
  }

  // Basic formatting based on country
  switch (countryCode) {
    case '+1': // US/Canada
      if (cleanNumber.length === 10) {
        return `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6)}`;
      }
      break;
    case '+91': // India
      if (cleanNumber.length === 10) {
        return `${cleanNumber.slice(0, 5)} ${cleanNumber.slice(5)}`;
      }
      break;
    case '+44': // UK
      if (cleanNumber.length >= 10) {
        return `${cleanNumber.slice(0, 4)} ${cleanNumber.slice(4, 7)} ${cleanNumber.slice(7)}`;
      }
      break;
    default:
      return phoneNumber;
  }

  return phoneNumber;
};

/**
 * Get all available country codes for dropdown
 */
export const getCountryCodes = (): { code: string; country: string; iso2: string; flag: string }[] => {
  return phoneValidationRules.map(rule => ({
    code: rule.countryCode,
    country: rule.country,
    iso2: rule.iso2,
    flag: getCountryFlag(rule.iso2),
  }));
};

/**
 * Get placeholder text for phone input based on country
 */
export const getPhonePlaceholder = (countryCode: string): string => {
  const rule = getValidationRule(countryCode);
  return rule ? rule.example : 'Enter phone number';
};
